import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";

export default function Select({
  value,
  onChange,
  options = [],
  disabled = false,
  placeholder = "Select an option...",
  className = "",
  id,
  searchable = false,
  searchPlaceholder = "Search...",
  emptyResultsLabel = "No options found",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({
    top: 0,
    bottom: null,
    left: 0,
    width: 0,
    maxMenuHeight: 280,
    placement: "below",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);

  const closeMenu = useCallback(({ focusButton = false } = {}) => {
    setIsOpen(false);
    setSearchQuery("");
    if (focusButton) {
      buttonRef.current?.focus();
    }
  }, []);

  const selectedOption = options.find(opt => {
    const optValue = typeof opt === 'object' ? opt.value : opt;
    if (value === undefined || value === null) {
      return false;
    }
    return String(optValue) === String(value);
  });

  const displayValue = selectedOption
    ? (typeof selectedOption === 'object' ? selectedOption.label : selectedOption)
    : placeholder;

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const GAP = 4;
    const VIEWPORT_PAD = 8;
    /** Reserve for search field + outer py-1 padding (px, conservative) */
    const searchReserve = searchable ? 56 : 8;
    /** Ideal max height for the scrollable list (15rem) */
    const idealListMax = 240;
    const spaceBelow = window.innerHeight - rect.bottom - GAP - VIEWPORT_PAD;
    const spaceAbove = rect.top - GAP - VIEWPORT_PAD;

    const idealCap = idealListMax + searchReserve;
    /** Never exceed actual viewport space (avoids clipped, non-interactive menus in modals / CI). */
    const belowH = Math.min(idealCap, Math.max(0, spaceBelow));
    const aboveH = Math.min(idealCap, Math.max(0, spaceAbove));

    const preferBelow =
      belowH >= idealCap ||
      belowH >= aboveH ||
      (belowH >= 120 && belowH >= aboveH - 40);

    const maxWidth = Math.max(120, window.innerWidth - rect.left - VIEWPORT_PAD);
    const width = Math.min(rect.width, maxWidth);

    if (preferBelow && belowH > 0) {
      setMenuPos({
        top: rect.bottom + GAP,
        bottom: null,
        left: rect.left,
        width,
        maxMenuHeight: belowH,
        placement: "below",
      });
    } else if (aboveH > 0) {
      setMenuPos({
        top: null,
        bottom: window.innerHeight - rect.top + GAP,
        left: rect.left,
        width,
        maxMenuHeight: aboveH,
        placement: "above",
      });
    } else {
      setMenuPos({
        top: rect.bottom + GAP,
        bottom: null,
        left: rect.left,
        width,
        maxMenuHeight: Math.min(idealCap, Math.max(1, spaceBelow)),
        placement: "below",
      });
    }
  }, [searchable]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();

    function handleResizeOrScroll() {
      updateMenuPosition();
    }

    window.addEventListener("resize", handleResizeOrScroll);
    window.addEventListener("scroll", handleResizeOrScroll, true);

    return () => {
      window.removeEventListener("resize", handleResizeOrScroll);
      window.removeEventListener("scroll", handleResizeOrScroll, true);
    };
  }, [isOpen, updateMenuPosition]);

  const handleSelect = useCallback((optValue) => {
    onChange(optValue);
    closeMenu({ focusButton: true });
  }, [closeMenu, onChange]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (!isOpen) return;

      const btn = buttonRef.current;
      if (!btn) return;
      const menu = menuRef.current;
      if (!btn.contains(e.target) && (!menu || !menu.contains(e.target))) {
        closeMenu();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeMenu, isOpen]);

  /** Close when focus moves to another control (e.g. email field). Avoids stale open menus and prevents the trigger staying focused so Space/Enter toggles the list while the user thinks they are typing elsewhere. */
  useEffect(() => {
    if (!isOpen) return;
    function handleFocusIn(e) {
      const btn = buttonRef.current;
      const menu = menuRef.current;
      if (!btn) return;
      const t = e.target;
      if (btn.contains(t) || menu?.contains(t)) return;
      closeMenu();
    }
    document.addEventListener("focusin", handleFocusIn, true);
    return () => document.removeEventListener("focusin", handleFocusIn, true);
  }, [closeMenu, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!searchable) return;
    const timer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        closeMenu({ focusButton: true });
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const menuItems = menuRef.current?.querySelectorAll('[role="option"]');
        if (!menuItems || menuItems.length === 0) return;

        const currentIndex = Array.from(menuItems).findIndex(
          item => item === document.activeElement || item.getAttribute('aria-selected') === 'true'
        );

        let nextIndex;
        if (e.key === 'ArrowDown') {
          nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
        }

        menuItems[nextIndex]?.focus();
      }

      if (e.key === 'Enter' || e.key === ' ') {
        const focused = document.activeElement;
        if (focused && focused.getAttribute('role') === 'option') {
          e.preventDefault();
          const optValue = focused.getAttribute('data-value');
          if (optValue) {
            handleSelect(optValue);
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeMenu, handleSelect, isOpen]);

  const normalizedOptions = options.map(opt => {
    if (typeof opt === 'object') {
      return { value: opt.value, label: opt.label, disabled: opt.disabled || false };
    }
    return { value: opt, label: opt, disabled: false };
  });
  const normalizedQuery = String(searchQuery || "").trim().toLowerCase();
  const visibleOptions = !searchable || !normalizedQuery
    ? normalizedOptions
    : normalizedOptions.filter((opt) => {
      const label = String(opt.label || "").toLowerCase();
      const valueText = String(opt.value || "").toLowerCase();
      return label.includes(normalizedQuery) || valueText.includes(normalizedQuery);
    });

  return (
    <>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        onClick={() => {
          if (disabled) return;
          if (isOpen) {
            closeMenu();
            return;
          }
          setSearchQuery("");
          setIsOpen(true);
        }}
        disabled={disabled}
        className={`
          relative w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:text-sm
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'hover:bg-gray-50'}
          ${className}
        `.trim()}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen && id ? `${id}-listbox` : undefined}
        aria-labelledby={id ? `${id}-label` : undefined}
      >
        <span className="block truncate">{displayValue}</span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <svg
            className="h-5 w-5 text-gray-400"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04L10 14.148l2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[100] flex min-h-0 min-w-0 flex-col overflow-hidden rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            style={{
              left: `${menuPos.left}px`,
              width: `${menuPos.width}px`,
              maxHeight: `${menuPos.maxMenuHeight}px`,
              ...(menuPos.placement === "below" && menuPos.top != null
                ? { top: `${menuPos.top}px` }
                : {}),
              ...(menuPos.placement === "above" && menuPos.bottom != null
                ? { bottom: `${menuPos.bottom}px` }
                : {}),
            }}
          >
            {searchable ? (
              <div className="shrink-0 border-b border-slate-200 p-2">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <div
              id={id ? `${id}-listbox` : undefined}
              className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]"
              role="listbox"
            >
              {normalizedOptions.length === 0 ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                  No options available
                </div>
              ) : visibleOptions.length === 0 ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                  {emptyResultsLabel}
                </div>
              ) : (
                visibleOptions.map((opt) => {
                const isSelected = value !== undefined && value !== null && String(opt.value) === String(value);
                const isDisabled = opt.disabled;

                return (
                  <div
                    key={opt.value}
                    role="option"
                    data-value={opt.value}
                    tabIndex={isDisabled ? -1 : 0}
                    onClick={() => !isDisabled && handleSelect(opt.value)}
                    onMouseDown={(e) => {
                      if (!isDisabled) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelect(opt.value);
                      }
                    }}
                    aria-selected={isSelected}
                    className={`
                      relative cursor-pointer select-none py-2 pl-3 pr-9
                      ${isSelected
                        ? 'bg-blue-50 text-blue-900'
                        : 'text-gray-900'
                      }
                      ${isDisabled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-gray-100'
                      }
                    `.trim()}
                  >
                    <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>
                      {opt.label}
                    </span>
                    {isSelected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.704 5.29a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 011.414-1.414l2.793 2.793 6.793-6.793a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    )}
                  </div>
                );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
